import { useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiInformationOutline, mdiLoading, mdiPlusCircleOutline } from "@mdi/js";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";
import * as Yup from "yup";

import Button from "components/Button";
import Checkbox from "components/Checkbox";
import Input from "components/Input";
import Select from "components/Select";
import Toggle from "components/Toggle";
import Tooltip from "components/Tooltip";
import { strings } from "constants/strings";
import AddCustomerModal from "features/main/HaulPay/modules/Shared/AddCustomerModal";
import { DECLINED_DEBTOR_NON_FACTORED_LOCKED_NOTICE, isDeclinedDebtor } from "helpers/debtorCredit";
import { formatNumber, formatAccounting } from "helpers/formatNumber";
import useDeviceType from "hooks/useDeviceType";
import { IOption } from "models/Form";
import { ICompany, IFactoringProfile, InformationFormData } from "models/HaulPay";
import { calculateFRAPI } from "services/api/haulpay";
import { selectFactoringProfile } from "store/user/selector";
import AddInvoiceDetailModal from "./modules/AddInvoiceDetailModal";
import EnterAdvanceAmountModal from "./modules/EnterAdvanceAmountModal";
import styles from "./style.module.scss";
import DebtorSearch from "components/DebtorSearchInput";
import Icon from "@mdi/react";
import debounce from "common/debounce";
import DollarInput from "components/DollarInput";
import Banner from "components/Banner";
import { useUserFeature } from "hooks/useUserFeature";
import dollarsToCents from "helpers/dollarsToCents";

const FUEL_ADVANCE_MAX_AMOUNT = 1500;

const getFuelAdvanceLimit = (totalInvoiceAmount?: number) => {
  if (typeof totalInvoiceAmount !== "number" || Number.isNaN(totalInvoiceAmount)) {
    return FUEL_ADVANCE_MAX_AMOUNT;
  }
  return Math.min(totalInvoiceAmount * 0.5, FUEL_ADVANCE_MAX_AMOUNT);
};

const informationFormSchema = Yup.object<Partial<InformationFormData>>({
  totalInvoiceAmount: Yup.number()
    .typeError("This field is required")
    .required("This field is required")
    .positive("Must be a positive number")
    .max(9999999.99, "Exceeds maximum amount")
    .test("max-decimals", "Only 2 decimals allowed", (value) => {
      return value ? /^\d+(\.\d{1,2})?$/.test(value.toString()) : true;
    }),
  loadNumber: Yup.string().required("This field is required"),
  advanceAmount: Yup.number()
    .nullable()
    .typeError("This field is required")
    .moreThan(0, "Advance amount must be greater than 0"),
  billToCompany: Yup.object()
    .required("This field is required")
    .shape({
      dba: Yup.string().required("Company name is required")
    }),
  fuelAdvanceAmount: Yup.number()
    .nullable()
    .when(
      ["isFuelAdvance", "totalInvoiceAmount"],
      ([isFuelAdvance, totalInvoiceAmount], schema) => {
        if (!isFuelAdvance) {
          return schema.nullable();
        }
        return schema
          .typeError("This field is required")
          .required("This field is required")
          .positive("Must be a positive number")
          .test("max-decimals", "Only 2 decimals allowed", (value) => {
            return value ? /^\d+(\.\d{1,2})?$/.test(value.toString()) : false;
          })
          .test("fuel-advance-limit", function (value) {
            const limit = getFuelAdvanceLimit(totalInvoiceAmount);
            if (value === undefined || value === null) return false;
            return value <= limit
              ? true
              : this.createError({
                  message: `Fuel advance amount must be less or equal to ${formatNumber(limit, 2)}`
                });
          })
          .test("fuel-advance-limit-pennies", function (value) {
            const limit = getFuelAdvanceLimit(totalInvoiceAmount);
            return value !== undefined ? dollarsToCents(value) <= dollarsToCents(limit) : true;
          });
      }
    )
});

interface PayInformationProps {
  companyValue?: ICompany;
  informationFormData?: InformationFormData;
  setIsShowSearch: (show: boolean) => void;
  handleNext: (data?: InformationFormData) => Promise<void>;
  setFormDataCallback: (data: InformationFormData, isValid?: boolean) => void;
}

export default function PayInformation({
  informationFormData,
  handleNext,
  setFormDataCallback
}: Readonly<PayInformationProps>) {
  const methods = useForm<Partial<InformationFormData>>({
    resolver: yupResolver(informationFormSchema),
    defaultValues: { ...informationFormData }
  });
  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    clearErrors,
    watch,
    control,
    formState: { errors, isSubmitting }
  } = methods;
  const isNonFactored = watch("isNonFactoredInvoice");
  const totalAmount = watch("totalInvoiceAmount");
  const advanceAmount = watch("advanceAmount");
  const isFuelAdvance = watch("isFuelAdvance");
  const billToCompany = watch("billToCompany");
  const isNonFactoredInvoice = watch("isNonFactoredInvoice");
  const isDeclinedBillToCompany = isDeclinedDebtor(billToCompany);

  const factoringProfile: IFactoringProfile = useSelector(selectFactoringProfile);
  const factoringFuelAdvance = factoringProfile?.fuel_advance;
  const [yourRate, setYourRate] = useState("0%");
  const [totalToBePaid, setTotalToBePaid] = useState(0);
  const [clientAdvanceFee, setClientAdvanceFee] = useState(0);
  const [isDisabledFuelAdvance, setIsDisabledFuelAdvance] = useState(true);
  const [isShowNonFactoredInvoice, setIsShowNonFactoredInvoice] = useState(true);

  const [isAddCustomerModalOpen, toggleAddCustomerModal] = useToggle();
  const [isInvoiceDetailAddModalOpen, toggleInvoiceDetailAddModal] = useToggle();
  const [confirmAdvanceAmountModal, setConfirmAdvanceAmountModal] = useState({
    isOpen: false,
    handleClose: () => {},
    handleSubmit: () => {}
  });

  const { isDesktop } = useDeviceType();
  const { isNonFactorRefactorEnabled } = useUserFeature();

  const onSubmit = async (_data: InformationFormData) => {
    const isAdvanceAmountPaid = await new Promise((resolve) =>
      setConfirmAdvanceAmountModal({
        isOpen: true,
        handleClose: () => {
          setValue("advanceAmount", undefined);
          resolve(false);
          setConfirmAdvanceAmountModal((prevState) => ({
            ...prevState,
            isOpen: false
          }));
        },
        handleSubmit: () => {
          resolve(true);
          setConfirmAdvanceAmountModal((prevState) => ({
            ...prevState,
            isOpen: false
          }));
        }
      })
    );
    setFormDataCallback(_data as InformationFormData);
    if (isAdvanceAmountPaid) {
      return await handleNext(_data as InformationFormData);
    }
    await handleNext(_data as InformationFormData);
  };
  const messageForValidBillToCompany = !getValues("billToCompany")
    ? strings.CARRIER_FR.ADD_DEBTOR_TO_FUEL_ADVANCE
    : !billToCompany?.noa_active
      ? strings.CARRIER_FR.FUEL_ADVANCE_NOA_NOT_PLACED
      : !billToCompany?.fuel_advance
        ? strings.CARRIER_FR.FUEL_ADVANCE_UNAVAILABLE
        : "";

  const paymentMethods: IOption[] = useMemo(() => {
    if (factoringProfile) {
      setValue("discountRate", factoringProfile.discount_rate);
      const sortedAccounts = [...factoringProfile.accounts].sort((a, b) => {
        if (a.method === "ACH") return -1;
        if (b.method === "ACH") return 1;
        return 0;
      });

      return sortedAccounts.map((account, index) => ({
        id: index,
        label: `${account.method} (**** ${account.account_number_redacted})`,
        value: `${account.method}${account.account_number_redacted}`
      }));
    } else {
      return [];
    }
  }, [factoringProfile]);

  const calculateFactoring = useMemo(
    () =>
      debounce(() => {
        if (billToCompany && totalAmount !== undefined) {
          calculateFRAPI({
            debtor: billToCompany?.id ?? "",
            factored: isNonFactored === undefined ? true : !isNonFactored,
            invoice_amount: dollarsToCents(totalAmount) - dollarsToCents(advanceAmount),
            factoring: factoringProfile?.id,
            create_rates_revision: false,
            contract_type: isNonFactored ? "NON_FACTORED_BILL_OUT" : "STD_CARRIER",
            discount_rate_comfreight: factoringProfile?.discount_rate,
            payment_profile_amount: 0
          }).then((response) => {
            const factoringPayable = (response.data.factoring_payable ?? 0) / 100;
            const apiAdvanceFee =
              (response.data.factored && response.data.client_advance_fee > 0
                ? response.data.client_advance_fee
                : response.data.factoring_fee ?? 0) / 100;

            setTotalToBePaid(factoringPayable);
            setClientAdvanceFee(apiAdvanceFee);
            const hasClientAdvanceFee = response.data?.client_advance_fee != null;
            const discountRateDisplay = hasClientAdvanceFee
              ? Number.parseFloat((response.data.discount_rate_comfreight * 100).toFixed(2))
              : Number.parseFloat(response.data.discount_rate_comfreight.toFixed(2));
            setYourRate(`${discountRateDisplay}%`);
          });
        }
      }, 300),
    [billToCompany, totalAmount, isNonFactored, advanceAmount, factoringProfile?.id, calculateFRAPI]
  );

  useEffect(() => {
    return () => calculateFactoring.cancel();
  }, [calculateFactoring]);

  useEffect(() => {
    calculateFactoring();
  }, [billToCompany, totalAmount, isNonFactored, advanceAmount, factoringProfile?.id]);

  useEffect(() => {
    if (!isFuelAdvance) {
      setValue("fuelAdvanceAmount", 0);
    }
  }, [isFuelAdvance, setValue]);

  useEffect(() => {
    if (isDeclinedBillToCompany && !isNonFactoredInvoice) {
      setValue("isNonFactoredInvoice", true);
    }
  }, [isDeclinedBillToCompany, isNonFactoredInvoice, setValue]);

  return factoringProfile ? (
    <FormProvider {...methods}>
      <form className={styles.payInformationContainer} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.body}>
          <h4 className={styles.header}>Information</h4>
          <div className={clsx(styles.paymentForm, "!border-none")}>
            <DollarInput
              name="totalInvoiceAmount"
              label="&nbsp;&nbsp;Total Invoice Amount"
              error={errors.totalInvoiceAmount}
              id="total-invoice-amount-input"
              data-cy="total-invoice-amount-input"
            />
            <Input
              name="loadNumber"
              register={register}
              label="&nbsp;&nbsp;Load Number"
              error={errors.loadNumber}
              id="load-number-input"
              data-cy="load-number-input"
            />
            <DebtorSearch
              name="billToCompany"
              onSelect={(company: ICompany) => {
                setValue("billToCompany", company);
                setIsDisabledFuelAdvance(
                  !factoringFuelAdvance || !company?.fuel_advance || !company?.noa_active
                );
                setIsShowNonFactoredInvoice(
                  company.credit_approved === "approved" || isDeclinedDebtor(company)
                );
                setValue("isFuelAdvance", false);
                clearErrors("billToCompany");
              }}
              label="&nbsp;&nbsp;Bill To Company"
              error={errors?.billToCompany as any}
              showAddNewCustomer
            />
            <Button
              type="button"
              variant="outline"
              className={styles.paymentSubmit}
              onClick={toggleAddCustomerModal}>
              New Customer
            </Button>
          </div>

          <div className={styles.paymentDetails}>
            <div className={styles.nonFactoredInvoice}>
              <button type="button" className="text-info" onClick={toggleInvoiceDetailAddModal}>
                + Add Invoice Details
              </button>
              <button
                type="button"
                className="desktop:hidden"
                onClick={toggleInvoiceDetailAddModal}>
                <Icon path={mdiPlusCircleOutline} />
              </button>
            </div>

            {factoringProfile.fuel_advance && (
              <div className={styles.nonFactoredInvoice}>
                <Toggle
                  suffix="Is this a fuel advance?"
                  className={clsx("-ml-3", isDisabledFuelAdvance && "cursor-not-allowed")}
                  name="isFuelAdvance"
                  disabled={isDisabledFuelAdvance}
                  register={register}
                />
                {isDisabledFuelAdvance ? (
                  <Tooltip
                    position={isDesktop ? "center" : "left"}
                    message={messageForValidBillToCompany}>
                    <Icon path={mdiInformationOutline} className="flex-none text-text" />
                  </Tooltip>
                ) : null}
              </div>
            )}
            {isShowNonFactoredInvoice && (
              <div className={styles.nonFactoredInvoice}>
                <Checkbox
                  name="isNonFactoredInvoice"
                  register={register}
                  error={errors.isNonFactoredInvoice}
                  disabled={isDeclinedBillToCompany}
                  wrapperClass="text-sm desktop:text-base">
                  This is a non-factored invoice?
                </Checkbox>
                <Tooltip
                  position="left"
                  message="We will process this like usual except you will get paid after we receive the money from your customer.">
                  <Icon path={mdiInformationOutline} className="flex-none text-text" />
                </Tooltip>
                {isDeclinedBillToCompany && (
                  <p
                    className="w-full mt-2 rounded-base bg-yellow-100 px-3 py-2 text-sm text-yellow-700"
                    data-cy="declined-debtor-notice">
                    {DECLINED_DEBTOR_NON_FACTORED_LOCKED_NOTICE}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className={styles.paymentMethod}>
            {paymentMethods.length ? (
              <Controller
                name="paymentMethod"
                defaultValue={paymentMethods[0]}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    options={paymentMethods}
                    onChange={onChange}
                    label="&nbsp;&nbsp;Payment Method"
                    data-cy="payment-method-select"
                  />
                )}
              />
            ) : null}
            {isFuelAdvance && (
              <DollarInput
                name="fuelAdvanceAmount"
                label="&nbsp;&nbsp;Fuel Advance Amount"
                error={errors.fuelAdvanceAmount}
                id="fuel-advance-amount-input"
                data-cy="fuel-advance-amount-input"
              />
            )}
          </div>
          <div className="flex w-3/4">
            {billToCompany?.originals_required && (
              <p className="text-info" style={{ fontSize: "12", paddingTop: 5 }}>
                This debtor requires copies of Originals Documents to be mailed to them directly.
                <br />
                Original Documents are physical copies of your actual signed bill of lading and any
                receipts or other paperwork associated with the load. <br />
                Please mail them to the debtor directly and notify invoice@haulpay.io once you have
                sent them.
              </p>
            )}
          </div>
          {isNonFactoredInvoice && isNonFactorRefactorEnabled && (
            <div className="mb-4 mobile:mx-4">
              <Banner
                variant="warning"
                message="Once the required documents (a signed Bill of Lading showing delivery or a TONU) are uploaded, this funding request will be invoiced at 8am, 12pm, and 5pm PST, provided your NOA is active with the debtor."
                dismissible
              />
            </div>
          )}
          <div className={styles.totalPayment}>
            {factoringProfile.minimum_fee / 100 !== clientAdvanceFee && !isNonFactoredInvoice && (
              <div className="flex justify-between">
                <span className={styles.rate}>{`Your Rate: ${yourRate}`}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span
                className={styles.rate}>{`Fees: ${formatAccounting(-clientAdvanceFee, 2)}`}</span>
            </div>
            <div className="flex justify-between">
              <div className="text-text">
                <span className="font-medium desktop:text-xl">Total </span>
                <span className="text-sm desktop:text-base">(To be Paid)</span>
              </div>
              <span className={styles.totalAmount}>{formatAccounting(totalToBePaid, 2)}</span>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <Button type="submit" disabled={isSubmitting}>
            {" "}
            {isSubmitting ? <Icon path={mdiLoading} size={1} spin={1} /> : "NEXT"}
          </Button>
        </div>

        <AddCustomerModal
          isAddModalOpen={isAddCustomerModalOpen}
          toggleAddModal={toggleAddCustomerModal}
        />

        <AddInvoiceDetailModal
          isOpen={isInvoiceDetailAddModalOpen}
          toggleModalOpen={toggleInvoiceDetailAddModal}
          factoringProfile={factoringProfile}
        />

        <EnterAdvanceAmountModal
          isOpen={confirmAdvanceAmountModal.isOpen}
          toggleModalOpen={confirmAdvanceAmountModal.handleClose}
          totalToBePaidAmount={totalToBePaid}
          onConfirm={async () => {
            confirmAdvanceAmountModal.handleSubmit();
          }}
          onDecline={confirmAdvanceAmountModal.handleClose}
        />
      </form>
    </FormProvider>
  ) : null;
}
